import { useEffect, useMemo, useRef, useState } from 'react';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import { useChatStore } from '@store/chat';
import { Button } from '@components/amc/Button';
import * as S from '@styles/chat/chatGroupChannelFooter.style';
import { Oval } from 'react-loader-spinner';
import { IconCircleXFilled, IconCircleArrowUpFilled } from '@tabler/icons-react';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TYPING_TIMEOUT = 3000;
const LINE_HEIGHT = 20;
const MIN_LINES = 1;
const MAX_LINES = 8;
const GroupChannelFooterInput = ({
  channelInputRef,
  imageFiles,
  isSendingImage,
  sendImageMessage,
  setImageFiles
}) => {
  const {
    state: {
      currentChannel
    },
    actions: {
      sendUserMessage,
      scrollToBottom
    }
  } = useGroupChannel();
  const {
    clickedMessageToReply,
    setClickedMessageToReply
  } = useChatStore();
  const typingTimeoutRef = useRef(null);
  const isComposingRef = useRef(false);
  const scrollTimerIdRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [hasText, setHasText] = useState(false);
  const imageFilesUrls = useMemo(() => imageFiles.map(file => URL.createObjectURL(file)), [imageFiles]);
  const handleRemoveImage = indexToRemove => {
    setImageFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };
  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };
  const handleCompositionEnd = () => {
    isComposingRef.current = false;
  };
  const adjustTextareaHeight = () => {
    if (!channelInputRef.current) return;
    const textarea = channelInputRef.current;
    textarea.style.overflow = 'hidden';
    textarea.style.height = 'auto';
    const textHeight = textarea.scrollHeight;
    const newRows = Math.min(Math.max(Math.ceil(textHeight / LINE_HEIGHT), MIN_LINES), MAX_LINES);
    textarea.style.overflow = 'auto';
    textarea.style.height = `${newRows * LINE_HEIGHT}px`;
  };
  const handleSendMessage = async () => {
    if (isSendingImage) return;
    const message = channelInputRef.current?.value.trim();
    const hasImages = imageFiles.length > 0;
    if (!message && !hasImages) return;
    if (hasImages) {
      await sendImageMessage(imageFiles);
      setImageFiles([]);
    }
    if (!message.trim()) return;
    const params = clickedMessageToReply ? {
      message,
      parentMessageId: clickedMessageToReply.messageId,
      isReplyToChannel: true
    } : {
      message,
      parentMessageId: null
    };
    sendUserMessage(params);
    if (channelInputRef.current) {
      channelInputRef.current.value = '';
    }
    currentChannel?.endTyping();
    if (scrollTimerIdRef.current) {
      clearTimeout(scrollTimerIdRef.current);
    }
    const delay = clickedMessageToReply ? 1000 : 10;
    scrollTimerIdRef.current = setTimeout(() => {
      scrollToBottom(true);
      scrollTimerIdRef.current = null;
    }, delay);
    setClickedMessageToReply(null);
    setHasText(false);
    adjustTextareaHeight();
  };
  const handleKeyDown = e => {
    if (isComposingRef.current) return;
    channelInputRef.current.value = e.target.value;
    const message = channelInputRef.current?.value;
    if (e.key === 'Enter' && !e.shiftKey && message?.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleTyping = () => {
    if (!currentChannel) return;
    currentChannel.startTyping();
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      currentChannel.endTyping();
    }, TYPING_TIMEOUT);
    setHasText(!!channelInputRef.current?.value.trim());
    adjustTextareaHeight();
  };
  const handlePaste = e => {
    const items = e.clipboardData.items;
    let imageFound = false;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const imageFile = item.getAsFile();
        if (imageFile) {
          setImageFiles(prevFiles => [...prevFiles, imageFile]);
          imageFound = true;
        }
        if (imageFound) return;
      }
    }
  };
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setClickedMessageToReply(null);
    };
  }, []);
  useEffect(() => {
    return () => {
      if (scrollTimerIdRef.current) {
        clearTimeout(scrollTimerIdRef.current);
      }
    };
  }, []);
  useEffect(() => {
    return () => {
      imageFilesUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imageFiles]);
  const isSendableMessage = hasText || imageFiles.length > 0;
  return /*#__PURE__*/_jsxs(S.MessageInput, {
    isFocused: isFocused,
    minHeight: `${LINE_HEIGHT}px`,
    maxHeight: `${LINE_HEIGHT * MAX_LINES}px`,
    children: [/*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("textarea", {
        rows: 1,
        ref: channelInputRef,
        placeholder: "\uBA54\uC2DC\uC9C0\uB97C \uC785\uB825\uD558\uC138\uC694.",
        onKeyDown: handleKeyDown,
        onChange: handleTyping,
        onCompositionStart: handleCompositionStart,
        onCompositionEnd: handleCompositionEnd,
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        onPaste: handlePaste
      }), imageFiles.length > 0 && /*#__PURE__*/_jsx(S.ImagePreviewContainer, {
        children: imageFilesUrls.map((url, index) => /*#__PURE__*/_jsxs("div", {
          className: "image-preview-item",
          children: [/*#__PURE__*/_jsx("img", {
            src: url,
            alt: imageFiles[index].name
          }), /*#__PURE__*/_jsx("button", {
            className: "remove-button",
            "aria-label": "\uC774\uBBF8\uC9C0 \uC0AD\uC81C",
            onClick: () => handleRemoveImage(index),
            children: /*#__PURE__*/_jsx(IconCircleXFilled, {
              size: 16,
              color: "black"
            })
          })]
        }, index))
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "send-button",
      children: isSendingImage ? /*#__PURE__*/_jsx(Oval, {
        height: 24,
        width: 24,
        color: "var(--blue-50)",
        secondaryColor: "var(--gray-80)",
        visible: true,
        ariaLabel: "oval-loading",
        strokeWidth: 4,
        strokeWidthSecondary: 4,
        wrapperClass: "!w-8 !h-8 !p-0 flex justify-center items-center"
      }) : /*#__PURE__*/_jsx(Button, {
        variant: "Ghost",
        icon: /*#__PURE__*/_jsx(IconCircleArrowUpFilled, {}),
        size: "Medium",
        onClick: isSendableMessage ? handleSendMessage : null,
        disabled: !isSendableMessage
      })
    })]
  });
};
export default GroupChannelFooterInput;